/**
 * Wake Lock API utilities to keep screen awake during workouts
 */

let wakeLock: WakeLockSentinel | null = null;

/**
 * Check if Wake Lock API is available
 */
export function isWakeLockAvailable(): boolean {
  return 'wakeLock' in navigator;
}

/**
 * Request wake lock to keep screen awake
 */
export async function requestWakeLock(): Promise<boolean> {
  if (!isWakeLockAvailable()) {
    console.warn('Wake Lock API not available');
    return false;
  }

  try {
    wakeLock = await navigator.wakeLock.request('screen');

    wakeLock.addEventListener('release', () => {
      console.log('Wake lock released');
    });

    console.log('Wake lock acquired');
    return true;
  } catch (err) {
    console.error('Failed to acquire wake lock:', err);
    return false;
  }
}

/**
 * Release wake lock
 */
export async function releaseWakeLock(): Promise<void> {
  if (wakeLock) {
    try {
      await wakeLock.release();
      wakeLock = null;
      console.log('Wake lock manually released');
    } catch (err) {
      console.error('Failed to release wake lock:', err);
    }
  }
}

/**
 * Get current wake lock status
 */
export function isWakeLockActive(): boolean {
  return wakeLock !== null && !wakeLock.released;
}

/**
 * Re-acquire wake lock after visibility change
 * (Wake locks are automatically released when page is hidden)
 */
export function setupWakeLockReacquisition(): void {
  if (!isWakeLockAvailable()) return;

  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible' && wakeLock?.released) {
      await requestWakeLock();
    }
  });
}
